/* eslint-disable react/no-unused-state */ // TODO: investigate if the state is needed, e.g. to trigger re-rendering
import React from 'react';
import { deviceType } from 'detect-it';
import {
  ArrowIconLeft,
  ArrowIconRight,
  Arrows,
  HorizontalScrollContainer,
  Inner,
  LeftArrow,
  List,
  Loader,
  RightArrow,
} from './styles';
import {
  LOAD_MORE_ITEMS_LIMIT,
  MOCK_DIV,
  SHADOW_OVERLAY,
  TRANSITION_TIME_MAX,
  TRANSITION_TIME_MIN,
} from './constants';
import { noop, animationUtils } from '@prisjakt/ui-base';
import { Spinner } from 'src/components/spinner';
import { ButtonIcon } from '@prisjakt/ui/button/variants';

export class HorizontalScroll extends React.Component<
  HorizontalScrollProps,
  HorizontalScrollState
> {
  scrollTimeout: undefined | number;

  scrollContainer: HTMLDivElement;

  outerContainer: HTMLDivElement;

  innerContainer: HTMLDivElement | HTMLUListElement;

  constructor(props: HorizontalScrollProps) {
    super(props);

    this.scrollTimeout = undefined;
    this.outerContainer = MOCK_DIV;
    this.scrollContainer = MOCK_DIV;
    this.innerContainer = MOCK_DIV;

    this.state = {
      mounted: false,
      touching: false,
      leftShadow: false,
      rightShadow: true,
      clientWidth: undefined,
      rightArrowVisible: Boolean(
        props.showArrows && React.Children.count(props.children) > 1
      ),
      leftArrowVisible: false,
      itemWidth: 0,
    };
  }

  static defaultProps = {
    loading: false,
    loadingHeight: 250,
    itemHint: false,
    maxItems: Infinity,
    showArrows: ['hybrid', 'mouseOnly'].includes(deviceType),
    onNeedData: noop,
  };

  componentDidUpdate(prevProps: HorizontalScrollProps) {
    const { showArrows, itemHint, itemWidth } = this.props;
    const { count } = React.Children;

    // Need to update arrow states when new children has arrived
    if (
      showArrows &&
      count(this.props.children) !== count(prevProps.children)
    ) {
      const { scrollLeft, clientWidth } = this.scrollContainer;
      const itemsLeft = this.getItemsLeft(scrollLeft + clientWidth);

      this.toggleArrows(scrollLeft, itemsLeft);
    }

    if (
      prevProps.itemHint !== itemHint ||
      (itemHint && prevProps.itemWidth !== itemWidth)
    ) {
      this.calculateWidth();
    }
  }

  resizeContainer = () => {
    this.setState({
      clientWidth: 0,
    });

    const { clientWidth } = this.outerContainer;

    this.setState({
      clientWidth,
    });
  };

  componentDidMount() {
    const { itemHint } = this.props;
    const pagination = this.getPagination();
    const { clientWidth, scrollLeft } = this.outerContainer;

    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      clientWidth,
      mounted: true,
    });

    if (itemHint) {
      this.calculateWidth();
    }

    if (pagination.current === 1 && pagination.pages === 1) {
      this.toggleArrows(
        scrollLeft,
        this.getItemsLeft(scrollLeft + clientWidth)
      );
    }

    window.addEventListener('resize', this.resizeContainer);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeContainer);

    this.setState({
      mounted: false,
    });
  }

  calculateWidth() {
    const { clientWidth } = this.scrollContainer;
    const { itemWidth } = this.props;

    if (!clientWidth) {
      return;
    }
    const space = clientWidth - itemWidth / 2;
    const spread = space / itemWidth;
    const items = Math.round(spread);
    const newWidth = itemWidth * (spread / items);

    this.setState({
      itemWidth: newWidth,
    });
  }

  togglePage(direction: 'prev' | 'next') {
    const { scrollLeft } = this.scrollContainer;
    const { current, itemsPerPage } = this.getPagination(scrollLeft);

    let goToPage = current - 1;

    switch (direction) {
      case 'prev':
        goToPage -= 1;
        break;
      case 'next':
        goToPage += 1;
        break;
      default:
        return;
    }

    this.scrollTo({
      from: scrollLeft,
      to: goToPage * itemsPerPage * this.getItemWidth(),
    });

    this.handleScroll();
  }

  getItemWidth() {
    return this.state.itemWidth || this.props.itemWidth;
  }

  getItemsLeft(scrollLeft: number) {
    const itemsLeft = Math.floor(scrollLeft / this.getItemWidth());
    return Math.max(
      0,
      React.Children.count(this.props.children) - 1 - itemsLeft
    );
  }

  getPagination(scrollLeft = 0) {
    const itemWidth = this.getItemWidth();
    const itemsPerPage = Math.floor(
      this.scrollContainer.clientWidth / itemWidth
    );
    const numberOfPages = Math.max(
      Math.floor(React.Children.count(this.props.children) / itemsPerPage),
      1
    );

    return {
      itemsPerPage,
      current: Math.floor(
        (scrollLeft + this.scrollContainer.clientWidth) /
          itemWidth /
          itemsPerPage
      ),
      pages: numberOfPages,
    };
  }

  toggleArrows(scrollLeft: number, itemsLeft: number) {
    const leftArrowVisible = Math.floor(scrollLeft / this.getItemWidth()) > 0;
    const rightArrowVisible =
      (itemsLeft !== 0 &&
        (this.props.maxItems ?? Infinity) >
          React.Children.count(this.props.children)) ||
      Boolean(itemsLeft);

    if (this.state.rightArrowVisible !== rightArrowVisible) {
      this.setState({
        rightArrowVisible,
      });
    }

    if (this.state.leftArrowVisible !== leftArrowVisible) {
      this.setState({
        leftArrowVisible,
      });
    }
  }

  handleScroll() {
    const { scrollWidth, scrollLeft, clientWidth } = this.scrollContainer;
    const scrollRight = scrollWidth - (clientWidth + scrollLeft);
    const { maxItems, onNeedData, loading, showArrows } = this.props;
    const { leftShadow, rightShadow } = this.state;
    const totalChilds = React.Children.count(this.props.children);
    const itemsLeft = this.getItemsLeft(scrollLeft + clientWidth);

    const needMoreData =
      onNeedData &&
      totalChilds <= (maxItems ?? Infinity) - 1 &&
      !loading &&
      itemsLeft <= LOAD_MORE_ITEMS_LIMIT;

    // Trigger more data callback if callback is registered and items left are under limit
    if (needMoreData && onNeedData) {
      onNeedData();
    }

    if (showArrows) {
      this.toggleArrows(scrollLeft, itemsLeft);
    }

    if (
      (!leftShadow && scrollLeft >= SHADOW_OVERLAY) ||
      (leftShadow && scrollLeft < SHADOW_OVERLAY)
    ) {
      this.setState({
        leftShadow: !leftShadow,
      });
    }

    if (
      (!rightShadow && scrollRight > SHADOW_OVERLAY) ||
      (rightShadow && scrollRight < SHADOW_OVERLAY)
    ) {
      this.setState({
        rightShadow: !rightShadow,
      });
    }
  }

  handleTouchDown() {
    this.setState({
      touching: true,
    });
  }

  handleTouchEnd() {
    this.setState({
      touching: false,
    });
  }

  scrollTo({ from, to }: { from: number; to: number }) {
    if (from === to) return;

    const { scrollContainer } = this;
    const distanceNormalize = Math.abs(to - from) / TRANSITION_TIME_MIN;
    const duration = Math.max(
      TRANSITION_TIME_MIN,
      Math.min(TRANSITION_TIME_MAX, TRANSITION_TIME_MIN * distanceNormalize)
    );

    animationUtils.tween({
      from,
      to,
      duration,
      onUpdate: (value: number) => {
        scrollContainer.scrollLeft = Math.round(value);
      },
    });
  }

  getScrollWidth() {
    if (!this.state.mounted) return '100%';

    const itemWidth = this.getItemWidth();
    const items = React.Children.count(this.props.children);
    const clientWidth =
      this.scrollContainer && this.scrollContainer.clientWidth;

    return Math.max(
      clientWidth || 0,
      itemWidth * items + (this.props.loading ? 100 : 0)
    );
  }

  prevPage = () => this.togglePage('prev');

  nextPage = () => this.togglePage('next');

  render() {
    const itemWidth = this.getItemWidth();
    const { children, showArrows } = this.props;
    const {
      leftShadow,
      rightShadow,
      leftArrowVisible,
      rightArrowVisible,
    } = this.state;

    return (
      <HorizontalScrollContainer
        ref={div => {
          this.outerContainer = div || MOCK_DIV;
        }}
        leftShadow={leftShadow}
        rightShadow={rightShadow}
        data-test="HorizontalScroll"
      >
        <Inner
          ref={div => {
            this.scrollContainer = div || MOCK_DIV;
          }}
          onScroll={() => this.handleScroll()}
          onTouchStart={() => this.handleTouchDown()}
          onTouchEnd={() => this.handleTouchEnd()}
        >
          {showArrows && (
            <Arrows>
              <LeftArrow onClick={this.prevPage} hidden={!leftArrowVisible}>
                <ButtonIcon>
                  <ArrowIconLeft />
                </ButtonIcon>
              </LeftArrow>
              <RightArrow onClick={this.nextPage} hidden={!rightArrowVisible}>
                <ButtonIcon>
                  <ArrowIconRight />
                </ButtonIcon>
              </RightArrow>
            </Arrows>
          )}
          <List
            ref={div => {
              this.innerContainer = div || MOCK_DIV;
            }}
            style={{
              width: this.getScrollWidth(),
            }}
          >
            {React.Children.map(children, (child, i) => (
              <li
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                style={{ flex: `0 0 ${itemWidth}px` }}
              >
                {child}
              </li>
            ))}
            {this.props.loading && (
              <Loader style={{ lineHeight: `${this.props.loadingHeight}px` }}>
                <Spinner position="none" isActive />
              </Loader>
            )}
          </List>
        </Inner>
      </HorizontalScrollContainer>
    );
  }
}

interface HorizontalScrollState {
  mounted: boolean;
  touching: boolean;
  leftShadow: boolean;
  rightShadow: boolean;
  clientWidth: undefined | number;
  rightArrowVisible: boolean;
  leftArrowVisible: boolean;
  itemWidth: number;
}

interface HorizontalScrollProps {
  itemWidth: number;
  loadingHeight?: number;
  onNeedData?: () => void;
  loading?: boolean;
  showArrows?: boolean;
  maxItems?: number;
  itemHint?: boolean;
  children: React.ReactNode;
}
